import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScoreZone, SecurityLevel } from '@/types/auth';
import { getScoreZone, getScoreColor, getZoneLabel } from '@/lib/score-utils';

interface UseScoreOptions {
  /** Security level */
  securityLevel?: SecurityLevel;
  /** Animate score changes */
  animate?: boolean;
  /** Animation duration in ms */
  animationDuration?: number;
}

interface UseScoreReturn {
  /** Displayed score (animated) */
  displayScore: number;
  /** Target score (final value) */
  targetScore: number;
  /** Score zone */
  zone: ScoreZone;
  /** Zone label */
  zoneLabel: string;
  /** Dynamic color for the score */
  color: string;
  /** Whether the score is currently animating */
  isAnimating: boolean;
  /** Set a new target score */
  setScore: (score: number) => void;
  /** Reset score */
  resetScore: () => void;
}

export function useScore(options: UseScoreOptions = {}): UseScoreReturn {
  const {
    securityLevel = 'balanced',
    animate = true,
    animationDuration = 800,
  } = options;

  const [targetScore, setTargetScore] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef(0);
  const startScoreRef = useRef(0);

  const zone = getScoreZone(displayScore, securityLevel);
  const zoneLabel = getZoneLabel(zone);
  const color = getScoreColor(displayScore);

  // Animate score changes
  useEffect(() => {
    if (!animate) {
      setDisplayScore(targetScore);
      return;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startTimeRef.current = performance.now();
    startScoreRef.current = displayScore;
    setIsAnimating(true);

    const animateFrame = (now: number) => {
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / animationDuration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      const current =
        startScoreRef.current + (targetScore - startScoreRef.current) * eased;
      setDisplayScore(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateFrame);
      } else {
        setDisplayScore(targetScore);
        setIsAnimating(false);
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animateFrame);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetScore, animate, animationDuration]);

  const setScore = useCallback((score: number) => {
    setTargetScore(Math.max(0, Math.min(100, score)));
  }, []);

  const resetScore = useCallback(() => {
    setTargetScore(0);
    setDisplayScore(0);
    setIsAnimating(false);
  }, []);

  return {
    displayScore,
    targetScore,
    zone,
    zoneLabel,
    color,
    isAnimating,
    setScore,
    resetScore,
  };
}
